/**
 * Owner PM Management Component
 * Allows project owners to view PM applications, manage authority, and approve payment requests
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Chip,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar
} from '@mui/material';
import {
    PersonAdd,
    Settings,
    AttachMoney,
    CheckCircle,
    Cancel,
    People,
    Description,
    History,
    PictureAsPdf,
    LoginRounded,
    LogoutRounded,
    RequestQuote
} from '@mui/icons-material';
import projectManagerApi from '../../services/projectManagerApi';
import PMAuthoritySelector from '../projects/PMAuthoritySelector';
import PMPaymentRequests from './PMPaymentRequests';
import DialogCloseButton from '../common/DialogCloseButton';

const OwnerPMManagement = ({ projectId }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [applications, setApplications] = useState([]);
    const [authoritySettings, setAuthoritySettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [authorityConfig, setAuthorityConfig] = useState(null);
    const [reports, setReports] = useState([]);
    const [activity, setActivity] = useState([]);

    useEffect(() => {
        if (projectId) {
            loadData();
        }
    }, [projectId]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            // Load PM applications
            const appsResponse = await projectManagerApi.getProjectApplications(projectId);
            if (appsResponse.success) {
                setApplications(appsResponse.data || []);
            }

            // Load authority settings
            const authResponse = await projectManagerApi.getAuthoritySettings(projectId);
            if (authResponse.success) {
                setAuthoritySettings(authResponse.data);
                if (authResponse.data) {
                    setAuthorityConfig({
                        authorityLevel: authResponse.data.authority_level,
                        autoApproveLimit: parseFloat(authResponse.data.auto_approve_limit),
                        weeklyLimit: parseFloat(authResponse.data.weekly_limit),
                        requireReceipts: authResponse.data.require_receipts,
                        requirePhotos: authResponse.data.require_photos,
                        requireGPS: authResponse.data.require_gps,
                        allowCashPayments: authResponse.data.allow_cash_payments,
                        maxCashAmount: parseFloat(authResponse.data.max_cash_amount),
                        notifyOnAllPayments: authResponse.data.notify_on_all_payments,
                        notifyOnThreshold: authResponse.data.notify_on_threshold,
                        dailySummary: authResponse.data.daily_summary
                    });
                }
            }

            // Load reports and activity (non-fatal if either fails)
            try {
                const reportsResponse = await projectManagerApi.getReports(projectId);
                if (reportsResponse.success) setReports(reportsResponse.data || []);
            } catch (e) {
                console.error('Error loading reports:', e);
            }
            try {
                const activityResponse = await projectManagerApi.getActivity(projectId);
                if (activityResponse.success) setActivity(activityResponse.data || []);
            } catch (e) {
                console.error('Error loading activity:', e);
            }
        } catch (err) {
            console.error('Error loading PM management data:', err);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const REPORT_TYPE_LABEL = {
        progress: 'Weekly Progress Report', monthly: 'Monthly Summary', inspection: 'Site Inspection Report',
        budget: 'Budget Analysis', milestone: 'Milestone Completion Report', client_update: 'Client Update Brief'
    };

    const activityIcon = (action) => {
        switch (action) {
            case 'site_check_in': return <LoginRounded color="primary" />;
            case 'site_check_out': return <LogoutRounded color="action" />;
            case 'payment_request_created': return <RequestQuote color="warning" />;
            case 'report_generated': return <Description color="success" />;
            case 'authority_changed': return <Settings color="info" />;
            default: return <History />;
        }
    };

    const handleApproveApplication = async () => {
        if (!selectedApplication || !authorityConfig) return;

        try {
            const response = await projectManagerApi.respondToApplication(
                selectedApplication.id,
                'approve',
                {
                    response: 'Welcome aboard!',
                    authoritySettings: authorityConfig
                }
            );
            if (response.success) {
                setApproveDialogOpen(false);
                setSelectedApplication(null);
                loadData();
            }
        } catch (err) {
            console.error('Error approving application:', err);
            setError(err.message || 'Failed to approve application');
        }
    };

    const handleRejectApplication = async (applicationId) => {
        if (!window.confirm('Are you sure you want to reject this application?')) return;

        try {
            const response = await projectManagerApi.respondToApplication(
                applicationId,
                'reject',
                { response: 'Thank you for your interest. We have selected another candidate.' }
            );
            if (response.success) {
                loadData();
            }
        } catch (err) {
            console.error('Error rejecting application:', err);
            setError(err.message || 'Failed to reject application');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Project Manager Management
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab icon={<PersonAdd />} label="Applications" iconPosition="start" />
                <Tab icon={<Settings />} label="Authority Settings" iconPosition="start" />
                <Tab icon={<AttachMoney />} label="Payment Requests" iconPosition="start" />
                <Tab icon={<Description />} label="Reports" iconPosition="start" />
                <Tab icon={<History />} label="Activity" iconPosition="start" />
            </Tabs>

            {/* Applications Tab */}
            {activeTab === 0 && (
                <Box>
                    {applications.length === 0 ? (
                        <Alert severity="info">No PM applications for this project</Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {applications.map((app) => (
                                <Grid item xs={12} key={app.id}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Avatar>
                                                        {app.projectManager?.firstName?.[0]}{app.projectManager?.lastName?.[0]}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h6">
                                                            {app.projectManager?.firstName} {app.projectManager?.lastName}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {app.projectManager?.email}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {app.projectManager?.phone}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip 
                                                    label={app.status} 
                                                    color={app.status === 'pending' ? 'warning' : app.status === 'accepted' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                <strong>Proposal:</strong> {app.proposal_message}
                                            </Typography>

                                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                <Chip 
                                                    label={`Fee: ${app.proposed_fee_percentage}%`} 
                                                    size="small" 
                                                />
                                                <Chip 
                                                    label={`Authority: ${app.proposed_authority_level}`} 
                                                    size="small" 
                                                />
                                            </Box>

                                            {app.status === 'pending' && (
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleRejectApplication(app.id)}
                                                    >
                                                        Reject
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => {
                                                            setSelectedApplication(app);
                                                            setApproveDialogOpen(true);
                                                        }}
                                                    >
                                                        Review & Approve
                                                    </Button>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            )}

            {/* Authority Settings Tab */}
            {activeTab === 1 && (
                <Box>
                    {authoritySettings ? (
                        <PMAuthoritySelector
                            projectId={projectId}
                            currentSettings={authorityConfig}
                            onSettingsChange={async (settings) => {
                                try {
                                    const response = await projectManagerApi.updateAuthoritySettings(projectId, settings);
                                    if (response.success) {
                                        setAuthorityConfig(settings);
                                        loadData();
                                    }
                                } catch (err) {
                                    console.error('Error updating authority:', err);
                                    setError(err.message || 'Failed to update authority settings');
                                }
                            }}
                        />
                    ) : (
                        <Alert severity="info">
                            No Project Manager assigned yet. Approve an application to configure authority settings.
                        </Alert>
                    )}
                </Box>
            )}

            {/* Payment Requests Tab */}
            {activeTab === 2 && (
                <Box>
                    <PMPaymentRequests projectId={projectId} isOwner={true} />
                </Box>
            )}

            {/* Reports Tab */}
            {activeTab === 3 && (
                <Box>
                    {reports.length === 0 ? (
                        <Alert severity="info">Your project manager hasn't filed any reports yet.</Alert>
                    ) : (
                        <List>
                            {reports.map((r) => (
                                <ListItem
                                    key={r.id}
                                    divider
                                    secondaryAction={
                                        <Button
                                            size="small"
                                            startIcon={<PictureAsPdf />}
                                            onClick={() => window.open(projectManagerApi.getReportPdfUrl(r.id), '_blank')}
                                        >
                                            View PDF
                                        </Button>
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar><Description /></Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={REPORT_TYPE_LABEL[r.report_type] || 'Project Report'}
                                        secondary={
                                            <>
                                                {new Date(r.created_at).toLocaleDateString()}
                                                {r.sent_to_client ? ' • Sent to client' : ''}
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            )}

            {/* Activity Tab */}
            {activeTab === 4 && (
                <Box>
                    {activity.length === 0 ? (
                        <Alert severity="info">No project manager activity recorded yet.</Alert>
                    ) : (
                        <List>
                            {activity.map((a) => (
                                <ListItem key={a.id} divider>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'transparent' }}>{activityIcon(a.action)}</Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={a.summary || a.action}
                                        secondary={new Date(a.created_at).toLocaleString()}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            )}

            {/* Approve Application Dialog */}
            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setApproveDialogOpen(false)} />
                <DialogTitle>Approve Project Manager Application</DialogTitle>
                <DialogContent>
                    {selectedApplication && (
                        <>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Configure the authority settings for {selectedApplication.projectManager?.firstName} {selectedApplication.projectManager?.lastName}
                            </Alert>
                            <PMAuthoritySelector
                                projectId={projectId}
                                currentSettings={authorityConfig || {
                                    authorityLevel: selectedApplication.proposed_authority_level,
                                    autoApproveLimit: 2000,
                                    weeklyLimit: 10000,
                                    requireReceipts: true,
                                    requirePhotos: true,
                                    requireGPS: true,
                                    allowCashPayments: false,
                                    maxCashAmount: 500,
                                    notifyOnAllPayments: true,
                                    notifyOnThreshold: true,
                                    dailySummary: false
                                }}
                                onSettingsChange={setAuthorityConfig}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleApproveApplication}
                        disabled={!authorityConfig}
                    >
                        Approve & Assign
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OwnerPMManagement;



